// Adapted from '@atlassian/jira-issue-table/src/common/fields/issue-key';

import React, { memo, useRef } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import type { ResolutionCell$key } from '@atlassian/jira-relay/src/__generated__/ResolutionCell.graphql';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

type Props = {
	fieldRef: ResolutionCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const ResolutionCell = memo<Props>(({ fieldRef, isReadOnlyWithoutHoverPopover }) => {
	const { formatMessage } = useIntl();
	const data = useFragment<ResolutionCell$key>(
		graphql`
			fragment ResolutionCell on JiraResolutionField
			@argumentDefinitions(
				isSingleLineEventDelegatorEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-single-line-event-delegator-enabled.relayprovider"
				}
			) {
				id @include(if: $isSingleLineEventDelegatorEnabled)
				resolution {
					name
				}
			}
		`,
		fieldRef,
	);

	const { resolution } = data;

	const isSingleLineRowHeightEnabled =
		useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;
	const contentRef = useRef<HTMLDivElement>(null);
	const hoverPopoverRefFactory =
		isSingleLineRowHeightEnabled && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

	const text = resolution?.name ?? formatMessage(messages.unresolved);

	if (!isSingleLineRowHeightEnabled)
		return <Cell data-vc="native-issue-table-ui-resolution-cell">{text}</Cell>;

	if (fg('issue_table_single_line_row_height_event_delegator')) {
		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory && fg('issue_table_single_line_row_height_event_delegator')) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: text,
			});
		}

		return (
			<Cell data-vc="native-issue-table-ui-resolution-cell" ref={hoverPopoverRef}>
				{text}
			</Cell>
		);
	}

	return (
		<HoverPopoverWithContext content={text} contentRef={contentRef}>
			{({ ref }) => (
				<Cell data-vc="native-issue-table-ui-resolution-cell" ref={mergeRefs(contentRef, ref)}>
					{text}
				</Cell>
			)}
		</HoverPopoverWithContext>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Cell = styled.div({
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
